import { type ServiceParams, unwrapJson } from "../client";

/**
 * Eval harness service — wraps the playground eval endpoints.
 *
 * Mirrors the SAC factory pattern used by the other API modules.
 * Eval runs are real provider calls (BYOK) — surface that to the UI
 * so operators understand the cost shape of ``k`` attempts.
 */

export type EvalCategory = "pii" | "policy" | "security";
export type EvalMetric = "pass@k" | "pass^k";
export type EvalResult = "PASS" | "FAIL";

export interface EvalExpect {
  pii_flagged?: boolean;
  pii_types_include?: string[];
  policy_blocked?: boolean;
  security_flagged?: boolean;
  no_content?: true;
}

export interface EvalScenario {
  id: string;
  skill_id: string;
  workspace_id: string;
  name: string;
  category: EvalCategory;
  input_message: string;
  expect: EvalExpect;
  metric: EvalMetric;
  k: number;
  created_at: string;
  updated_at: string;
}

export interface EvalAttempt {
  passed: boolean;
  request_id: string;
  checks: Record<string, boolean>;
  observed: Record<string, unknown>;
  terminal_error: { type: string; code: string; message: string } | null;
  governance_steps: Array<{
    step: string;
    status: string;
    meta: string;
    ms: number;
  }>;
}

export interface EvalRun {
  id: string;
  scenario_id: string;
  skill_id: string;
  skill_version: number;
  metric: EvalMetric;
  k: number;
  attempts: number;
  passes: number;
  result: EvalResult;
  evidence: { attempts: EvalAttempt[] };
  created_at: string;
}

export interface CreateScenarioRequest {
  skill_id: string;
  name: string;
  category: EvalCategory;
  input_message: string;
  expect: EvalExpect;
  metric?: EvalMetric;
  k?: number;
}

export interface UpdateScenarioRequest {
  name?: string;
  input_message?: string;
  expect?: EvalExpect;
  metric?: EvalMetric;
  k?: number;
}

const EvalEndpoint = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/playground/eval`;

  return {
    async listScenarios(skillId?: string): Promise<EvalScenario[]> {
      const qs = skillId ? `?skill_id=${encodeURIComponent(skillId)}` : "";
      const response = await fetch(`${base}/scenarios${qs}`, {
        method: "GET",
        headers: config(),
      });
      const body = await unwrapJson<{ items: EvalScenario[] }>(response);
      return body.items;
    },

    async createScenario(payload: CreateScenarioRequest): Promise<EvalScenario> {
      const response = await fetch(`${base}/scenarios`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<EvalScenario>(response);
    },

    async updateScenario(
      scenarioId: string,
      payload: UpdateScenarioRequest
    ): Promise<EvalScenario> {
      const response = await fetch(`${base}/scenarios/${scenarioId}`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<EvalScenario>(response);
    },

    async deleteScenario(scenarioId: string): Promise<void> {
      const response = await fetch(`${base}/scenarios/${scenarioId}`, {
        method: "DELETE",
        headers: config(),
      });
      if (!response.ok && response.status !== 204) {
        await unwrapJson(response);
      }
    },

    async runScenario(
      scenarioId: string,
      skillVersion: number
    ): Promise<EvalRun> {
      const response = await fetch(`${base}/scenarios/${scenarioId}/run`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify({ skill_version: skillVersion }),
      });
      return unwrapJson<EvalRun>(response);
    },

    async runBatch(skillId: string, skillVersion: number): Promise<EvalRun[]> {
      const response = await fetch(`${base}/run-batch`, {
        method: "POST",
        headers: config(),
        body: JSON.stringify({ skill_id: skillId, skill_version: skillVersion }),
      });
      const body = await unwrapJson<{ runs: EvalRun[] }>(response);
      return body.runs;
    },

    async listRuns(params: {
      skillId?: string;
      skillVersion?: number;
      scenarioId?: string;
      limit?: number;
    }): Promise<EvalRun[]> {
      const qs = new URLSearchParams();
      if (params.skillId) qs.set("skill_id", params.skillId);
      if (params.skillVersion !== undefined)
        qs.set("skill_version", String(params.skillVersion));
      if (params.scenarioId) qs.set("scenario_id", params.scenarioId);
      if (params.limit !== undefined) qs.set("limit", String(params.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const response = await fetch(`${base}/runs${suffix}`, {
        method: "GET",
        headers: config(),
      });
      const body = await unwrapJson<{ items: EvalRun[] }>(response);
      return body.items;
    },
  };
};

export default EvalEndpoint;
